import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Réalisez-vous des miroirs sur mesure à Lyon ?",
    a: "Oui, nous fabriquons des miroirs sur mesure selon vos dimensions et vos besoins : miroir de salle de bain, dressing, décoration murale, verre argenté ou teinté. Nous assurons la découpe, le façonnage des bords, et la pose.",
  },
  {
    q: "Quels types de verre proposez-vous pour la décoration ?",
    a: "Nous proposons du verre clair, extra-clair, teinté ou dépoli. Nous réalisons des découpes de précision pour tablettes en verre, crédences de cuisine, portes de meuble vitrées, et tous types de verre décoratif sur mesure.",
  },
  {
    q: "Intervenez-vous pour des miroirs dans les commerces à Lyon ?",
    a: "Oui, nous travaillons aussi bien pour les particuliers que pour les professionnels à Lyon : miroirs de salle de sport, de salon de coiffure, de boutique, ou de restaurant. Nous nous adaptons à tous les projets.",
  },
];

export default function Miroiterie() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Miroiterie à Lyon : miroirs et verre sur mesure',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Miroiterie à Lyon : miroirs sur mesure, verre décoratif, découpe de précision. Pose soignée. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Vitrier à Lyon', item: 'https://depanfenetres-lyon.fr/vitrier-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Miroiterie', item: 'https://depanfenetres-lyon.fr/miroiterie-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Miroiterie à Lyon : miroirs et verre sur mesure | DFPRO"
        description="Miroiterie à Lyon : miroirs sur mesure, verre décoratif, découpe et pose. Miroir de salle de bain, dressing, crédence. Devis gratuit. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/miroiterie-lyon/"
        ogTitle="Miroiterie à Lyon — Miroirs et verre sur mesure | DFPRO"
        ogDescription="Miroirs, verre décoratif, découpe sur mesure à Lyon. Pose soignée, devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/vitrier-lyon/" className="hover:text-primary-500 transition-colors">Vitrier à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Miroiterie</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/miroiterie-hero-2026.jpg"
              alt="Miroiterie à Lyon — miroirs sur mesure et verre décoratif par DFPRO Lyon"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Miroiterie à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Miroirs sur mesure, verre décoratif, découpe de précision. De la salle de bain au dressing, nous réalisons vos projets de miroiterie à Lyon et dans la métropole.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Miroiterie à Lyon : du miroir sur mesure au verre décoratif
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    La miroiterie est un métier d'artisanat de précision qui ne se limite pas au remplacement de vitres. Chez DFPRO Lyon, nous mettons notre savoir-faire au service de vos projets décoratifs et fonctionnels : <strong className="text-foreground-800">miroirs sur mesure, verre décoratif, tablettes, crédences</strong>. Nous intervenons à Lyon et dans toute la Métropole de Lyon pour les particuliers comme pour les professionnels.
                  </p>
                  <p>
                    Nous réalisons des miroirs aux <strong className="text-foreground-800">dimensions exactes</strong> de votre espace : miroir de salle de bain au-dessus d'une vasque, miroir de dressing en pied, miroir mural décoratif de grand format, ou encore miroir avec bords biseautés pour une finition élégante. Nos verres sont disponibles en argenté classique, mais aussi en teinté (bronze, fumé) pour une ambiance plus douce.
                  </p>
                  <p>
                    Au-delà des miroirs, notre atelier de miroiterie peut réaliser de nombreuses pièces en verre sur mesure : <strong className="text-foreground-800">tablettes en verre trempé</strong> pour bibliothèques ou meubles, <strong className="text-foreground-800">crédences de cuisine en verre</strong> peint ou laqué, <strong className="text-foreground-800">portes de meuble vitrées</strong>, ou encore des panneaux de verre décoratif. Chaque pièce est découpée aux dimensions précises avec des bords adoucis ou polis selon l'usage.
                  </p>
                  <p>
                    Nous travaillons également avec les professionnels à Lyon : boutiques, salons de coiffure, salles de sport, restaurants et hôtels. Nous réalisons des <strong className="text-foreground-800">miroirs muraux grand format</strong>, des verres de protection pour tables et comptoirs, et tout type d'aménagement vitré sur mesure. Notre équipe s'adapte à vos contraintes de planning, y compris pour intervenir en dehors des heures d'ouverture.
                  </p>
                  <p>
                    La pose est incluse dans notre prestation. Nos vitriers se déplacent chez vous à Lyon, Villeurbanne, Caluire, Vénissieux, Bron et toutes les communes de la métropole pour prendre les mesures, fabriquer la pièce, et assurer une pose parfaite. Chaque réalisation bénéficie de notre <strong className="text-foreground-800">garantie 10 ans</strong>.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services vitrier</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Vitrier à Lyon (pilier)', href: '/vitrier-lyon/' },
                    { label: 'Remplacement vitre cassée', href: '/remplacement-vitre-cassee-lyon/' },
                    { label: 'Remplacement double vitrage', href: '/remplacement-double-vitrage-lyon/' },
                    { label: 'Survitrage', href: '/survitrage-lyon/' },
                    { label: 'Vitrier urgence', href: '/vitrier-urgence-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Un projet de miroiterie à Lyon ?"
          subtitle="Miroir sur mesure, verre décoratif ? Appelez le 09 80 80 36 84 pour un devis gratuit."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}